import { useState, useEffect, useMemo } from "react";
import { useQuery, useLazyQuery } from "@apollo/client";
import { ORDERS_QUERY } from "../queries";
import { Variables } from "../types";
import EmptyState from "@/components/EmptyState";
import Table, { TableDetails, type Row, buildActionColumn } from "@/components/Table";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { toFormat } from "@/lib/dateHelpers";
import useCapabilities, { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useNavigate, Path } from "@/router";
import usePagination from "@/hooks/usePagination";
import { Controller, useForm } from "react-hook-form";
import SearchInput from "@/components/SearchInput";
import { intersectionBy, reject, isEmpty } from "lodash";
import { ChangeDueDateModal } from "./ChangeDueDateModal";
import { TerminateModal } from "./TerminateModal";
import { SuspendModal } from "./SuspendModal";
import { RowSelectionState, Table as TableComponent } from "@tanstack/react-table";
import Chip from "@/components/Chip";
import Button from "@/components/Button";
import pluralize from "@/lib/pluralize";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import MultiSelect from "@/components/MultiSelect";
import DateSelect from "@/components/DateSelect";
import { NumberFormatBase } from "react-number-format";
import TextField from "@/components/TextField";
import { isNull, get } from "lodash";
import FacilityAutocomplete from "../../../_components/FacilityAutocomplete";
import { FormProvider } from "react-hook-form";
import { ExpandedFiltersWrapper } from "@/components/ExpandedFilters";
import SectionAutocomplete from "../../../_components/SectionAutocomplete";
import { currencyFormatter } from "@/lib/currencyHelpers";
import { useSearch } from "@/hooks/useSearch";
import { renderStatus } from "@/pages/billing/_utils";
import { GET_CLASSIFICATIONS } from "../../../queries";
import { ClassificationType, StatsType } from "../../types";
import MetricGrid from "@/components/MetricGrid";
import { useParams } from "@/hooks/useSearchParams";
import { OrderRow, OrderNode } from "@/pages/billing/types";
import { buildColumns } from "@/pages/billing/_utils";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import Checkbox from "@/components/Checkbox";
import { useMutation } from "@apollo/client";
import { TERMINATE_PROGRAMS_MUTATION, SUSPEND_PROGRAMS_MUTATION, CHANGE_DUE_DATES_MUTATION } from "../queries";
import { showSnackbar } from "@/lib/snackbarUtils";
import { MaterialNames } from "@/components/material_symbol_names";
import MobileSortDrawer from "./MobileSortDrawer";
import { ColumnSort } from "@tanstack/react-table";
import useAmplitude from "@/hooks/useAmplitude";
import { BILLING_ANALYTICS_EVENTS, getBillingAnalyticsData } from "@/pages/billing/_utils/amplitude";

export type FormValues = {
  maxBalance: string;
  memberClassifications: string[];
  paymentStatuses: [];
  sectionIds: string[];
  facilityIds: string[];
  dueDateRange: { startDate: Date | null; endDate: Date | null };
};

const mapCommerceOrders = (data: OrderNode[]) =>
  (data || []).map(({ updatedAt, dueDate, balance, lineItems, ...order }) => ({
    ...order,
    pgaId: order.person.pgaId,
    classification: order.person.classification || "",
    section: order.person.primarySectionAffiliation?.section.name || "",
    updatedAt: formatDate(updatedAt),
    dueDate: formatDate(dueDate),
    balance: centsToDollarsFormatted(balance) || undefined,
    products: lineItems.length
  }));

const formatDate = (date?: string): string | undefined => (date && toFormat(date)) || undefined;

export const OrdersTable = ({ defaultVariables }: { defaultVariables: Variables }) => {
  useRequireCapability({ domain: DOMAIN.COMMERCE, permission: PERMISSION.viewOrders });
  const { trackEvent } = useAmplitude();
  const { hasPermission } = useCapabilities();
  const availableForSuspensions = Object.keys(defaultVariables)[0] === "availableForSuspensions";

  let { params, addSearchParam, deleteSearchParams, searchParamsUrl } = useParams();
  params.balanceRange = params.maxBalance && { to: Number(params.maxBalance) };
  params.dueDateRange = params.dueDateRange && { from: null, to: params.dueDateRange || null };

  const {
    searchValue,
    control: searchControl,
    reset: resetSearch,
    setValue: setSearchValue
  } = useSearch("name", params.search || "");

  const { renderFooter, paginationVariables, resetPagination } = usePagination();
  const [openChangeDueDateModal, setOpenChangeDueDateModal] = useState(false);
  const [openTerminateModal, setOpenTerminateModal] = useState(false);
  const [openSuspendModal, setOpenSuspendModal] = useState(false);
  const [terminateAll, setTerminateAll] = useState(false);
  const [suspendAll, setSuspendAll] = useState(false);

  const [selectedOrders, setSelectedOrders] = useState<any>();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const rowSelected = Object.keys(rowSelection).length;
  const [selectedData, setSelectedData] = useState<OrderRow[]>([]);
  const [isSelectedOnly, setIsSelectedOnly] = useState(false);
  const [isMobileSortOpen, setIsMobileSortOpen] = useState(false);
  const [orderBy, setOrderBy] = useState<ColumnSort[]>([]);

  const [selectedValues, setSelectedValues] = useState<string[]>(params.paymentStatuses || []);
  const [selectedClassificationValues, setSelectedClassificationValues] = useState<string[]>(
    params.memberClassifications || []
  );

  const [variables, setVariables] = useState<Variables>({ ...defaultVariables, ...params });
  const { data, loading, refetch } = useQuery(ORDERS_QUERY, { variables: { ...variables, ...paginationVariables } });
  const [selectedOrdersQuery, { data: allSelectedData, loading: allSelectedLoading }] = useLazyQuery(ORDERS_QUERY);
  const { data: classificationData } = useQuery(GET_CLASSIFICATIONS);
  const [terminateProgramsMutation, { loading: terminateLoading }] = useMutation(TERMINATE_PROGRAMS_MUTATION);
  const [suspendProgramsProgramsMutation, { loading: suspendLoading }] = useMutation(SUSPEND_PROGRAMS_MUTATION);
  const [changeDueDateMutation, { loading: changeDueDateLoading }] = useMutation(CHANGE_DUE_DATES_MUTATION);

  const navigate = useNavigate();
  const { Menu, setMenu } = useTableRowMenu();
  const { isCompactLayout } = useIsCompactLayout();

  const form = useForm<FormValues>();
  const { control, reset, watch, getValues, setValue } = form;

  const pattern = "/billing/orders";
  function onClickRow(row: Row<OrderRow>) {
    navigate(
      `${pattern}/${row.original.id}?return_to=/billing/dues-management/suspensions-and-terminations/${
        availableForSuspensions ? `suspensions&${searchParamsUrl}` : `terminations&${searchParamsUrl}`
      }` as Path
    );
  }

  const orderData: OrderRow[] = mapCommerceOrders(data?.duesOrders?.nodes || []);

  const allSelectedDataArray: OrderRow[] = mapCommerceOrders(allSelectedData?.duesOrders?.nodes || []);

  const getIsSomeRowsSelected = () => {
    return intersectionBy(selectedData, orderData, "id").length > 0 && selectedData.length !== orderData.length;
  };

  const [stats, setStats] = useState<StatsType>({
    totalCount: null,
    failedPayments: null
  });

  useEffect(() => {
    if (!loading && data) {
      if (stats.totalCount === null || stats.failedPayments == null) {
        setStats({
          totalCount: data.totalDuesOrders?.totalCount,
          failedPayments: data.totalAvailableDuesOrdersWithFailedPayments?.totalCount
        });
      }
      setValue("sectionIds", params.sectionIds);
      setValue("facilityIds", params.facilityIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);
  const sectionIds = watch("sectionIds");
  const facilityIds = watch("facilityIds");

  useEffect(() => {
    setVariables((prevValue) => ({
      ...prevValue,
      ...(sectionIds ? { sectionIds: sectionIds } : {}),
      ...(facilityIds ? { facilityIds: facilityIds } : {})
    }));
  }, [sectionIds, facilityIds]);
  const totalOrders = data?.totalDuesOrders?.totalCount;

  useEffect(() => {
    setRowSelection({});
  }, [totalOrders]);

  useEffect(() => {
    setVariables((prevValue) => ({
      ...prevValue,
      search: searchValue
    }));
  }, [searchValue, setVariables]);

  const filters = {
    maxBalance: !!getValues("maxBalance"),
    section: !!sectionIds,
    paymentStatuses: selectedValues.length > 0,
    memberClassifications: selectedClassificationValues.length > 0,
    dueDateRange: !!variables.dueDateRange?.to,
    facility: !!facilityIds
  };

  const classificationNodes = get(classificationData, "classifications.nodes");
  const classificationDataNodes =
    classificationNodes?.map((node: ClassificationType) => ({
      value: node.name,
      label: node.name
    })) || [];

  const onClearAll = () => {
    setVariables(defaultVariables);
    setSelectedValues([]);
    setSelectedClassificationValues([]);
    reset({
      dueDateRange: { startDate: null, endDate: null }
    });
    deleteSearchParams([
      "dueDateRange",
      "maxBalance",
      "memberClassifications[]",
      "paymentStatuses[]",
      "sectionIds[]",
      "section",
      "facilityIds[]",
      "facility"
    ]);
  };

  const [errorContent, setErrorContent] = useState("");
  const activeFilterTotalOrders = data?.duesOrders?.totalCount;
  async function terminatePrograms(inputVariables: any, close: () => void) {
    const updatedVariables = {
      ...inputVariables,
      ...(terminateAll && {
        ...(stats.totalCount !== activeFilterTotalOrders
          ? {
              ordersSearch: variables
            }
          : { terminateAll: true })
      })
    };

    const { data } = await terminateProgramsMutation({
      variables: { input: updatedVariables }
    });

    if (data.duesBulkTerminatePrograms.success) {
      const successfulPersonIds = data.duesBulkTerminatePrograms.successfulPersonIds.length;
      showSnackbar(`${successfulPersonIds} people have been terminated`);

      setStats({
        totalCount: null,
        failedPayments: null
      });
      if (updatedVariables.ordersSearch) {
        onClearAll();
        deleteSearchParams("search");
        resetSearch();
      } else {
        refetch();
      }
      if (data.duesBulkTerminatePrograms.failedPersonIds.length > 0) {
        setErrorContent(data.duesBulkTerminatePrograms.message);
        refetch();
      } else {
        close();
      }
      trackEvent(
        BILLING_ANALYTICS_EVENTS.programsTerminated,
        getBillingAnalyticsData({
          source: "suspensions_and_terminations",
          personCount: successfulPersonIds,
          action: terminateAll ? "terminate_all_or_filtered" : "terminate_selected"
        })
      );
    } else {
      setErrorContent(data.duesBulkTerminatePrograms.message);
    }
  }

  async function suspendPrograms(inputVariables: any, close: () => void) {
    const updatedVariables = {
      ...inputVariables,
      ...(suspendAll && {
        ...(stats.totalCount !== activeFilterTotalOrders
          ? {
              ordersSearch: variables
            }
          : { suspendAll: true })
      })
    };

    const { data } = await suspendProgramsProgramsMutation({
      variables: { input: updatedVariables }
    });

    if (data.duesBulkSuspendPrograms.success) {
      const successfulPersonIds = data.duesBulkSuspendPrograms.successfulPersonIds.length;
      showSnackbar(`${successfulPersonIds} people have been suspended`);

      setStats({
        totalCount: null,
        failedPayments: null
      });
      if (updatedVariables.ordersSearch) {
        onClearAll();
        deleteSearchParams("search");
        resetSearch();
      }
      refetch();
      if (data.duesBulkSuspendPrograms.failedPersonIds.length > 0) {
        setErrorContent(data.duesBulkSuspendPrograms.message);
        refetch();
      } else {
        close();
      }
      trackEvent(
        BILLING_ANALYTICS_EVENTS.programsSuspended,
        getBillingAnalyticsData({
          source: "suspensions_and_terminations",
          personCount: successfulPersonIds,
          action: suspendAll ? "suspend_all_or_filtered" : "suspend_selected"
        })
      );
    } else {
      setErrorContent(data.duesBulkSuspendPrograms.message);
    }
  }

  async function changeDueDate(variables: Variables, close: () => void, sendNotification: boolean) {
    const { data } = await changeDueDateMutation({
      variables: { input: variables }
    });

    if (data.commerceBulkChangeOrderDueDate.success) {
      const successfulOrders = data.commerceBulkChangeOrderDueDate.successfulOrders.length;
      showSnackbar(`${successfulOrders} ${pluralize(successfulOrders, "order")} due dates have been extended`);
      if (sendNotification) {
        showSnackbar("Email Notification Sent");
      }

      setStats({
        totalCount: null,
        failedPayments: null
      });
      refetch();
      if (data.commerceBulkChangeOrderDueDate.failedOrders.length > 0) {
        setErrorContent(data.commerceBulkChangeOrderDueDate.message);
        refetch();
      } else {
        close();
      }
      trackEvent(
        BILLING_ANALYTICS_EVENTS.bulkOrderDueDatesChanged,
        getBillingAnalyticsData({
          source: "suspensions_and_terminations",
          orderCount: successfulOrders,
          action: sendNotification ? "bulk_change_due_date_with_notification" : "bulk_change_due_date"
        })
      );
    } else {
      setErrorContent(data.commerceBulkChangeOrderDueDate.message);
    }
  }
  const ActionButton = useMemo(() => {
    return (
      <Button
        variant="filled"
        disabled={activeFilterTotalOrders === 0}
        onClick={() => {
          if (availableForSuspensions) {
            setSuspendAll(true);
            setOpenSuspendModal(true);
          } else {
            setTerminateAll(true);
            setOpenTerminateModal(true);
          }
          setSelectedOrders([]);
        }}
      >
        {availableForSuspensions ? "Suspend" : "Terminate"}{" "}
        {data?.totalDuesOrders?.totalCount === activeFilterTotalOrders || loading
          ? "All"
          : activeFilterTotalOrders + " People"}
      </Button>
    );
  }, [activeFilterTotalOrders, availableForSuspensions, loading, data]);

  return (
    <>
      {stats.totalCount === 0 ? (
        <EmptyState
          title={`No-one Eligible for ${availableForSuspensions ? "Suspension" : "Termination"} for Overdue Dues`}
          iconName="person"
        />
      ) : (
        <>
          <MetricGrid
            metrics={[
              {
                label: `Total Eligible for ${availableForSuspensions ? "Suspension" : "Termination"}`,
                value: `${!isNull(stats.totalCount) && stats.totalCount}`,
                loading: isNull(stats.totalCount),
                iconName: "person" as MaterialNames,
                ...(stats.totalCount !== null && stats.totalCount > 0 && { variant: "warn" as const })
              },
              {
                label: `Orders in Active Filter(s)`,
                value: `${activeFilterTotalOrders}`,
                loading: loading,
                iconName: "receipt_long" as MaterialNames
              },
              {
                label: "Total Orders with Failed Payment",
                value: `${!isNull(stats.failedPayments) && stats.failedPayments}`,
                variant:
                  stats.failedPayments !== null && stats.failedPayments !== undefined && stats.failedPayments > 0
                    ? "error"
                    : undefined,
                loading: isNull(stats.failedPayments),
                iconName: "receipt_long" as MaterialNames
              }
            ]}
            layout="expanded"
          />
          <Menu />
          <div className="w-full">
            <Table
              data={(isSelectedOnly ? allSelectedDataArray : orderData) || []}
              loading={loading || allSelectedLoading}
              onClickRow={onClickRow}
              rowSelection={rowSelection}
              onRowSelectionChange={setRowSelection}
              setSelectedData={setSelectedData}
              hasActiveFilters={Object.values(filters).some(Boolean) || !!(searchValue || "").trim()}
              renderDetails={() => {
                const bodyMessage = availableForSuspensions
                  ? "List of Dues orders that have not been fully paid and are overdue. Multi-select people to suspend a subset, or press “Suspend All” to start the suspension process for everyone."
                  : "List of Dues orders that have not been fully paid and are overdue by more than 30 days. Multi-select people to terminate a subset, or press “Terminate All” to start the termination process for everyone.";

                return (
                  <TableDetails
                    heading="Overdue Orders"
                    body={bodyMessage}
                    ButtonComps={rowSelected === 0 ? [() => ActionButton] : []}
                  />
                );
              }}
              renderHeader={(table: TableComponent<OrderRow>) => (
                <div>
                  <div className="flex md:items-center justify-between">
                    <div
                      className={`flex justify-between md:justify-start gap-3 md:flex-row w-full sm:pr-4 ${
                        rowSelected > 0 ? "flex-col" : "flex-row"
                      }`}
                    >
                      <FormProvider {...form}>
                        <div className="w-full sm:max-w-[320px] flex-shrink-1 md:flex-shrink-0 focus:outline-none">
                          <Controller
                            name="name"
                            control={searchControl}
                            render={({ field }) => (
                              <SearchInput
                                placeholder="Search Orders"
                                query={field.value}
                                onClear={() => {
                                  deleteSearchParams("search");
                                  setSearchValue("search", "");
                                }}
                                onChange={(e) => {
                                  field.onChange(e);
                                  resetPagination();
                                  addSearchParam("search", e);
                                }}
                              />
                            )}
                          />
                        </div>
                        {rowSelected === 0 && (
                          <ExpandedFiltersWrapper
                            filters={filters}
                            items={[
                              {
                                component: (
                                  <Controller
                                    name="maxBalance"
                                    control={control}
                                    defaultValue={params.maxBalance || ""}
                                    render={({ field }) => {
                                      return (
                                        <NumberFormatBase
                                          label="Max Balance"
                                          placeholder="Enter Amount"
                                          leadingIcon="attach_money"
                                          customInput={TextField}
                                          format={currencyFormatter}
                                          value={field.value ?? ""}
                                          name={field.name}
                                          onValueChange={(values) => {
                                            field.onChange(values.floatValue);
                                            if (values.floatValue) {
                                              setVariables((prevValue) => ({
                                                ...prevValue,
                                                balanceRange: { to: values.floatValue }
                                              }));
                                              addSearchParam("maxBalance", values.floatValue.toString());
                                            } else {
                                              setVariables((prevValue) => {
                                                const { balanceRange, ...rest } = prevValue;
                                                return {
                                                  ...rest
                                                };
                                              });
                                              deleteSearchParams("maxBalance");
                                            }
                                          }}
                                          className="w-full"
                                        />
                                      );
                                    }}
                                  />
                                )
                              },
                              {
                                component: (
                                  <SectionAutocomplete
                                    params={params}
                                    addSearchParam={addSearchParam}
                                    deleteSearchParams={deleteSearchParams}
                                    name="section"
                                  />
                                )
                              },
                              {
                                component: (
                                  <Controller
                                    name="memberClassifications"
                                    control={control}
                                    render={() => (
                                      <MultiSelect
                                        label="Classification"
                                        selectedValues={selectedClassificationValues}
                                        options={classificationDataNodes}
                                        onSelect={(value, checked) => {
                                          const status = checked
                                            ? [...selectedClassificationValues, value]
                                            : selectedClassificationValues.filter((v) => v !== value);
                                          setSelectedClassificationValues(reject(status, isEmpty));
                                          setVariables((prevValue) => ({
                                            ...prevValue,
                                            memberClassifications: reject(status, isEmpty)
                                          }));
                                          addSearchParam("memberClassifications[]", reject(status, isEmpty).toString());
                                        }}
                                        onClear={() => {
                                          setSelectedClassificationValues([]);
                                          setVariables((prevValue) => ({
                                            ...prevValue,
                                            memberClassifications: []
                                          }));
                                          deleteSearchParams("memberClassifications[]");
                                        }}
                                        className="w-full"
                                      />
                                    )}
                                  />
                                )
                              },
                              {
                                component: (
                                  <Controller
                                    name="paymentStatuses"
                                    control={control}
                                    render={() => (
                                      <MultiSelect
                                        label="Status"
                                        selectedValues={selectedValues}
                                        options={[
                                          { value: "HAS_FAILED_PAYMENTS", label: "Has Failed Payment" },
                                          { value: "HAS_REFUNDED_PAYMENTS", label: "Has Refunded Payment" }
                                        ]}
                                        onSelect={(value, checked) => {
                                          const status = checked
                                            ? [...selectedValues, value]
                                            : selectedValues.filter((v) => v !== value);

                                          setSelectedValues(reject(status, isEmpty));
                                          setVariables((prevValue) => ({
                                            ...prevValue,
                                            paymentStatuses: reject(status, isEmpty)
                                          }));
                                          addSearchParam("paymentStatuses[]", reject(status, isEmpty).toString());
                                        }}
                                        onClear={() => {
                                          setSelectedValues([]);
                                          setVariables((prevValue) => ({
                                            ...prevValue,
                                            paymentStatuses: []
                                          }));
                                          deleteSearchParams("paymentStatuses[]");
                                        }}
                                        className="w-full"
                                      />
                                    )}
                                  />
                                )
                              },
                              {
                                component: (
                                  <Controller
                                    name="dueDateRange"
                                    control={control}
                                    render={({ field }) => {
                                      return (
                                        <DateSelect
                                          placeholder="Select a Date"
                                          label="Due Date on or Before"
                                          useModal={true}
                                          value={field.value}
                                          useSingleDate
                                          onChange={(e) => {
                                            field.onChange(e);
                                            if (typeof e === "object" && e !== null) {
                                              setVariables((prevValue) => ({
                                                ...prevValue,
                                                dueDateRange: { from: null, to: e.endDate }
                                              }));
                                              addSearchParam("dueDateRange", e.endDate?.toString() || "");
                                            }
                                          }}
                                        />
                                      );
                                    }}
                                  />
                                )
                              },
                              {
                                component: (
                                  <FacilityAutocomplete
                                    sectionIds={variables?.sectionIds}
                                    params={params}
                                    addSearchParam={addSearchParam}
                                    deleteSearchParams={deleteSearchParams}
                                    name="facility"
                                  />
                                )
                              }
                            ]}
                            onClearAll={onClearAll}
                          />
                        )}
                      </FormProvider>

                      {rowSelected > 0 && hasPermission(PERMISSION.manageOrders) && (
                        <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap mt-3 sm:mt-0 ml-0 sm:ml-3">
                          {availableForSuspensions ? (
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setSelectedOrders(rowSelection);
                                setSuspendAll(false);
                                setOpenSuspendModal(true);
                              }}
                            >
                              Suspend Selected
                            </Button>
                          ) : (
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setSelectedOrders(rowSelection);
                                setTerminateAll(false);
                                setOpenTerminateModal(true);
                              }}
                            >
                              Terminate Selected
                            </Button>
                          )}
                          <Button
                            variant="outlined"
                            onClick={() => {
                              setSelectedOrders(rowSelection);
                              setOpenChangeDueDateModal(true);
                            }}
                          >
                            Change Due Date
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="hidden md:block">
                      {!isSelectedOnly &&
                        renderFooter(data?.duesOrders, {
                          totalCount: activeFilterTotalOrders || 0,
                          pageCount: orderData.length,
                          variant: "compact"
                        })()}
                    </div>
                  </div>

                  {rowSelected > 0 && (
                    <div className="flex items-center gap-3 mt-2 md:mt-3 flex-wrap md:flex-nowrap">
                      <Chip
                        onClick={() => {
                          setIsSelectedOnly(!isSelectedOnly);
                          selectedOrdersQuery({ variables: { ids: Object.keys(rowSelection) } });
                        }}
                        label="Show Selected Only"
                        selected={isSelectedOnly}
                      />
                      <Button
                        variant="text"
                        onClick={() => {
                          setRowSelection({});
                          setIsSelectedOnly(false);
                          resetPagination();
                        }}
                      >
                        Deselect All {rowSelected} {pluralize(rowSelected, "Row")}
                      </Button>
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-3 md:hidden">
                    <div className="flex items-center">
                      <Checkbox
                        name="select-all"
                        checked={table.getIsAllRowsSelected()}
                        indeterminate={table.getIsSomeRowsSelected()}
                        onChange={(checked) => table.toggleAllRowsSelected(checked)}
                      />
                    </div>
                    {!isSelectedOnly &&
                      renderFooter(data?.duesOrders, {
                        totalCount: activeFilterTotalOrders || 0,
                        pageCount: orderData.length,
                        variant: "compact"
                      })()}
                  </div>
                </div>
              )}
              columns={[
                ...buildColumns({
                  isCompactLayout,
                  hasCheckbox: true,
                  selectableRows: orderData,
                  getIsSomeRowsSelected,
                  setIsSelectedOnly
                }),
                { accessorKey: "pgaId", header: "PGA ID", enableSorting: false },
                { accessorKey: "classification", header: "Classification", enableSorting: false },
                { accessorKey: "section", header: "Section", enableSorting: false },
                {
                  accessorKey: "products",
                  header: "Products",
                  enableSorting: false
                },
                { accessorKey: "updatedAt", header: "Modified", enableSorting: false },
                { accessorKey: "dueDate", header: "Due Date", enableSorting: false },
                { accessorKey: "balance", header: "Balance", enableSorting: false },
                {
                  header: "Stage",
                  id: "stage",
                  cell: ({ row: { original } }) => renderStatus(original.status, original.paymentStatus),
                  size: 100
                },
                ...(hasPermission(PERMISSION.manageOrders)
                  ? [
                      buildActionColumn<OrderRow>({
                        getActions: (row) => [
                          {
                            text: "Change Due Date",
                            onClick: () => {
                              setSelectedOrders({ [`${row.id}`]: true });
                              setOpenChangeDueDateModal(true);
                            }
                          }
                        ],
                        actionId,
                        setMenu
                      })
                    ]
                  : [])
              ]}
              renderFilteredEmptyState={() => (
                <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="person" />
              )}
              renderFooter={() => (
                <div
                  className={`${
                    rowSelected > 0 ? "justify-between" : "justify-end"
                  } flex flex-col md:flex-row md:items-center`}
                >
                  {rowSelected > 0 && (
                    <div className="flex items-center gap-2 mb-3 md:mb-0">
                      <span className="text-label-large">
                        {rowSelected} {pluralize(rowSelected, "row")} selected
                      </span>
                      <Chip
                        onClick={() => {
                          setIsSelectedOnly(!isSelectedOnly);
                          selectedOrdersQuery({ variables: { ids: Object.keys(rowSelection) } });
                        }}
                        label="Show Selected Only"
                        selected={isSelectedOnly}
                      />
                    </div>
                  )}
                  {!isSelectedOnly &&
                    renderFooter(data?.duesOrders, {
                      totalCount: activeFilterTotalOrders || 0,
                      pageCount: orderData.length
                    })()}
                </div>
              )}
            />
          </div>
        </>
      )}
      {openChangeDueDateModal && (
        <ChangeDueDateModal
          onDismiss={() => {
            setOpenChangeDueDateModal(false);
          }}
          orders={selectedOrders}
          onSubmit={(variables, close, sendNotification = false) => changeDueDate(variables, close, sendNotification)}
          loading={changeDueDateLoading}
          errorContent={errorContent}
        />
      )}
      {openTerminateModal && (
        <TerminateModal
          onDismiss={() => {
            setOpenTerminateModal(false);
          }}
          orders={selectedOrders}
          terminateAll={terminateAll}
          totalPeople={activeFilterTotalOrders || 0}
          onSubmit={terminatePrograms}
          loading={terminateLoading}
          errorContent={errorContent}
        />
      )}
      {openSuspendModal && (
        <SuspendModal
          onDismiss={() => {
            setOpenSuspendModal(false);
          }}
          orders={selectedOrders || {}}
          suspendAll={suspendAll}
          totalPeople={activeFilterTotalOrders || 0}
          onSubmit={suspendPrograms}
          loading={suspendLoading}
          errorContent={errorContent}
        />
      )}

      <MobileSortDrawer
        isOpen={isMobileSortOpen}
        onClose={() => setIsMobileSortOpen(false)}
        onSortChange={setOrderBy}
        currentSort={orderBy}
      />
    </>
  );
};

export default OrdersTable;
